import * as React from 'react';
import { Button } from "@/components/ui/button";
import { IconResize, IconLink } from '@tabler/icons-react';
import { toast } from "sonner";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";

interface ImageResizeOptionsProps {
  imageUrl: string;
}

const ImageResizeOptions: React.FC<ImageResizeOptionsProps> = ({ imageUrl }) => {
  // State for tracking current resize parameters
  const [resizeParams, setResizeParams] = React.useState<{width?: string; height?: string}>({});
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const [actualDimensions, setActualDimensions] = React.useState<{width: number; height: number} | null>(null);

  // Generate the preview URL based on current parameters
  const getPreviewUrl = () => {
    if (!imageUrl) return '';
    const params = [];
    if (resizeParams.width) params.push(`w=${resizeParams.width}`);
    if (resizeParams.height) params.push(`h=${resizeParams.height}`);
    return params.length > 0 ? `${imageUrl}?${params.join('&')}` : imageUrl;
  };

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setActualDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    setImageLoaded(true);
  };

  return (
    <>
      <Separator className="my-4" />
      
      <div className="mb-2 font-semibold text-sm text-gray-700 flex items-center gap-2">
        <IconResize size={16} />
        <span>Modify Image</span>
      </div>
      
      <Card className="mt-2">
        <CardHeader className="py-3">
          <CardTitle className="text-sm">Resize Parameters</CardTitle>
          <div className="text-xs text-muted-foreground">Add these parameters to the URL to resize images on-the-fly</div>
        </CardHeader>
        <CardContent className="py-2">
          {/* Image Preview */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-600 mb-2 flex items-center justify-between">
              <span>Preview:</span>
              {(resizeParams.width || resizeParams.height) && (
                <a 
                  href={getPreviewUrl()} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  View actual size →
                </a>
              )}
            </div>
            <div className="flex justify-center">
              <img 
                src={getPreviewUrl()} 
                alt="Resized preview" 
                className="max-w-full max-h-64 object-contain border border-gray-200 rounded"
                onLoad={handleImageLoad}
                onError={(e) => {
                  e.currentTarget.src = imageUrl;
                  setImageLoaded(false);
                }}
              />
            </div>
            {imageLoaded && actualDimensions && (resizeParams.width || resizeParams.height) && (
              <div className="text-xs text-gray-600 text-center mt-2 space-y-1">
                <div className="font-medium">
                  Actual dimensions: {actualDimensions.width} × {actualDimensions.height}px
                </div>
                <div className="text-gray-500">
                  {actualDimensions.width > 800 || actualDimensions.height > 256 ? (
                    <span className="italic">Preview is scaled to fit. Click "View actual size" to see full resolution.</span>
                  ) : (
                    <span>Displaying at actual size</span>
                  )}
                </div>
              </div>
            )}
          </div>

          <Tabs defaultValue="examples" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="examples">Examples</TabsTrigger>
              <TabsTrigger value="custom">Custom Size</TabsTrigger>
            </TabsList>
            <TabsContent value="examples" className="pt-2">
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-xs justify-start" 
                      onClick={() => setResizeParams({ width: '100', height: '100' })}
                    >
                      Thumbnail (100×100)
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-xs justify-start" 
                      onClick={() => setResizeParams({ width: '300', height: undefined })}
                    >
                      Small (300px width)
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-xs justify-start" 
                      onClick={() => setResizeParams({ width: '800', height: undefined })}
                    >
                      Medium (800px width)
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-xs justify-start" 
                      onClick={() => setResizeParams({ width: '1200', height: undefined })}
                    >
                      Large (1200px width)
                    </Button>
                  </div>
                </div>
                
                <Button 
                  className="w-full text-xs" 
                  size="sm"
                  onClick={() => {
                    const url = getPreviewUrl();
                    if (url !== imageUrl) {
                      navigator.clipboard.writeText(url);
                      toast.success('URL copied!', { 
                        description: `Size preset URL copied to clipboard` 
                      });
                    }
                  }}
                  disabled={!resizeParams.width && !resizeParams.height}
                >
                  <div className="flex items-center justify-center w-full">
                    <IconLink size={14} className="mr-1.5" />
                    <span>Copy Resized URL</span>
                  </div>
                </Button>
              </div>
            </TabsContent>
            <TabsContent value="custom" className="pt-2">
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="width" className="text-xs">Width (px)</Label>
                    <Input 
                      id="width" 
                      placeholder="e.g., 800" 
                      className="text-xs" 
                      onChange={(e) => setResizeParams(prev => ({...prev, width: e.target.value || undefined}))}
                      value={resizeParams.width || ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="height" className="text-xs">Height (px)</Label>
                    <Input 
                      id="height" 
                      placeholder="e.g., 600" 
                      className="text-xs" 
                      onChange={(e) => setResizeParams(prev => ({...prev, height: e.target.value || undefined}))}
                      value={resizeParams.height || ''}
                    />
                  </div>
                </div>
                <Button 
                  className="w-full text-xs" 
                  size="sm"
                  onClick={() => {
                    const url = getPreviewUrl();
                    if (url !== imageUrl) {
                      navigator.clipboard.writeText(url);
                      toast.success('Custom URL copied!', { 
                        description: `URL with ${resizeParams.width ? 'width=' + resizeParams.width : ''}${resizeParams.width && resizeParams.height ? ' and ' : ''}${resizeParams.height ? 'height=' + resizeParams.height : ''} copied to clipboard` 
                      });
                    }
                  }}
                  disabled={!resizeParams.width && !resizeParams.height}
                >
                  <IconLink size={14} className="mr-1" />
                  Generate & Copy URL
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
        <CardFooter className="text-sm text-gray-500 py-2">
          Example: <span className="inline-block w-2"></span><code>image.cdn_url{resizeParams.width || resizeParams.height ? `?${resizeParams.width ? `w=${resizeParams.width}` : ''}${resizeParams.width && resizeParams.height ? '&' : ''}${resizeParams.height ? `h=${resizeParams.height}` : ''}` : ''}</code>
        </CardFooter>
      </Card>
    </>
  );
};

export default ImageResizeOptions;
